import { HTMLAttributes, ReactNode } from "react";
import { cx } from "../../utils/cx";
import { Progress } from "./Progress";
import type { ProgressSize } from "./Progress";
import { ProgressCircular } from "./ProgressCircular";
import styles from "./LoadingProgress.module.scss";

export type LoadingProgressIndicator = "linear" | "circular";

export type LoadingProgressProps = Omit<
    HTMLAttributes<HTMLDivElement>,
    "title"
> & {
    /** Main line (e.g. “Getting everything ready…”). */
    title: ReactNode;
    /** Optional supporting line(s) under the title. */
    subtitle?: ReactNode;
    /** Linear bar or circular ring (Figma Progress “Copy me” screens). */
    indicator?: LoadingProgressIndicator;
    /** Passed through to the embedded progress control. */
    value?: number;
    min?: number;
    max?: number;
    indeterminate?: boolean;
    /** Preset height / ring size. */
    size?: ProgressSize;
};

/**
 * Centered loading block: **title + subtitle + progress** (linear or circular),
 * matching Figma “Loading Treez” / “Getting everything ready” compositions.
 */
export function LoadingProgress({
    title,
    subtitle,
    indicator = "linear",
    value = 40,
    min = 0,
    max = 100,
    indeterminate = false,
    size = "md",
    className,
    ...rest
}: LoadingProgressProps) {
    return (
        <div
            className={cx(styles.root, className)}
            data-ui="loading-progress"
            {...rest}
        >
            {indicator === "linear" ? (
                <div className={styles.progressSlot}>
                    <Progress
                        fullWidth
                        indeterminate={indeterminate}
                        max={max}
                        min={min}
                        size={size}
                        value={value}
                    />
                </div>
            ) : (
                <div className={styles.progressSlot}>
                    <ProgressCircular
                        indeterminate={indeterminate}
                        max={max}
                        min={min}
                        size={size}
                        value={value}
                    />
                </div>
            )}
            <h2 className={styles.title}>{title}</h2>
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
        </div>
    );
}

LoadingProgress.displayName = "LoadingProgress";
